// apps/share-web/src/setlist-screen/guest-view.ts
//
// Гостевой вариант экрана сетлиста (единый сетлист, фаза 3, design gate G2) —
// `/s/<id>?g=<key>`, БЕЗ входа. Младший брат `view.ts` (участник команды): те
// же классы строки (`lib-song`/`lib-card-row`/`song-body`/row-controls) — одно
// правило для глаза, — но своя шапка (логотип + «Sign in», без бургера/языка/
// «Get the app» полной ah-* шапки: гостю сетлиста хватает одного действия) и
// свой хвост — подпись из одной строки о том, что публично, а что закрыто.
//
// Модуль ЧИСТЫЙ по данным: сеть, роутинг, разбор `?g=` и вычисление
// caption/subtitle остаются у будущей проводки (задача wiring, не design
// gate) — сюда приходит уже готовая модель. `renderGuestClosed` — соседний
// экран того же гостя: 401 по ключу (ссылку закрыли).
import { t } from '../ui/i18n.js';
import { icon } from '../ui/icons.js';
import { buildSongRowControls } from '../ui/song-row-controls.js';
import { buildEmptyState } from '../ui/empty-state.js';
import { BRAND_LOGO } from '../assets/brand-logo.js';

/** Строка гостевого экрана — подмножество ScreenRow (setlist-screen/model.ts)
 *  без songId/versions: гость сетлиста версию не выбирает (это плеер, задача
 *  «Version ▾»), сюда попадает уже РАЗРЕШЁННАЯ версия как `audioPath`. */
export interface GuestScreenRow {
  key: string;
  name: string;
  meta: string;
  /** Путь к словам, или null — слот пуст (дэш + причина). */
  wordsPath: string | null;
  /** Путь к аудио, или null — слот пуст (дэш + причина). Игнорируется при `audioLocked`. */
  audioPath: string | null;
  /** Запись ЕСТЬ на сервере, но закрыта этому гостю — слот с замком, тап → вход. */
  audioLocked: boolean;
}

export interface GuestSetlistVm {
  title: string;
  /** Готовая строка «Sun 28.09 · 5 songs» — без имени команды (оно только у
   *  участников, как в фазе 1); форматирование остаётся у вызывающего. */
  subtitle: string;
  /** Лидер ведёт сейчас — номер песни и куда ведёт пилюля. null — не ведёт. */
  live: { songNumber: number; path: string } | null;
  rows: GuestScreenRow[];
  /** Однострочная подпись внизу — зависит от флагов публичности; null — подписи нет. */
  caption: string | null;
}

export interface GuestSetlistHandlers {
  navigate: (path: string) => void;
  /** И шапка, и замок аудио ведут сюда одним обработчиком — оба хотят одного: войти. */
  onSignIn: () => void;
}

function buildGuestHeader(onSignIn: () => void): HTMLElement {
  const header = document.createElement('header');
  header.className = 'gss-header';
  header.dataset.role = 'guest-header';

  const brand = document.createElement('div');
  brand.className = 'gss-brand';
  brand.innerHTML = `<span class="gss-logo">${BRAND_LOGO}</span><span class="gss-brand-name">Band Way</span>`;
  header.appendChild(brand);

  const signin = document.createElement('bw-button');
  signin.setAttribute('data-action', 'signin');
  signin.setAttribute('shape', 'pill');
  signin.setAttribute('size', 'sm');
  signin.textContent = t('topbar.signIn');
  signin.addEventListener('bw-click', () => onSignIn());
  header.appendChild(signin);

  return header;
}

/** Строка песни — та же разметка, что buildRow у participant-экрана (view.ts)
 *  и у вкладки «Песни» библиотеки: карточка = слова, справа два органа,
 *  замок — третье состояние органа аудио (song-row-controls.ts, audioLocked). */
function buildRow(row: GuestScreenRow, isNow: boolean, opts: GuestSetlistHandlers): HTMLElement {
  const songEl = document.createElement('div');
  songEl.className = 'lib-song';
  songEl.dataset.songId = row.key;

  const cardRow = document.createElement('div');
  cardRow.dataset.role = 'song-row';
  cardRow.className = 'lib-card-row' + (isNow ? ' is-now' : '');

  const hasWords = row.wordsPath !== null;
  const hasAudio = row.audioPath !== null;

  const openWords = (): void => { if (row.wordsPath) opts.navigate(row.wordsPath); };

  const body = document.createElement('button');
  body.type = 'button';
  body.dataset.role = 'song-body';
  body.className = 'song-card song-body';

  const main = document.createElement('span');
  main.className = 'song-main';

  const nameEl = document.createElement('span');
  nameEl.className = 'song-name';
  nameEl.textContent = row.name || t('library.untitled');
  main.appendChild(nameEl);

  if (isNow || row.meta) {
    const metaEl = document.createElement('span');
    metaEl.dataset.role = 'song-meta';
    metaEl.className = 'song-sub';
    if (isNow) {
      const nowSpan = document.createElement('span');
      nowSpan.className = 'ss-now';
      nowSpan.textContent = t('setlistScreen.now');
      metaEl.appendChild(nowSpan);
      if (row.meta) metaEl.appendChild(document.createTextNode(' · ' + row.meta));
    } else {
      metaEl.textContent = row.meta;
    }
    main.appendChild(metaEl);
  }
  body.appendChild(main);

  if (hasWords) {
    body.addEventListener('click', openWords);
  } else {
    body.setAttribute('aria-disabled', 'true');
    // НЕ `is-disabled` (library.css: opacity 0.6) — орфографический выбор
    // участника (view.ts) здесь неверен для гостя. PRODUCT.md принцип 2:
    // «light the current place, never dim» — тусклый текст не способ что-то
    // сказать, а дашированный слот аудио/слов справа уже называет причину.
    // `gss-song-inert` снимает только кликабельность (cursor), не яркость
    // имени песни (критика оркестратора по 16073e7c, пункт 5; phase-1
    // member-экран (view.ts) остаётся с `is-disabled` — вне мандата этой
    // задачи, см. ту же критику).
    body.classList.add('gss-song-inert');
    body.title = t('library.wordsNotPublished');
  }
  cardRow.appendChild(body);

  const controls = buildSongRowControls({
    hasWords, hasAudio,
    audioLocked: row.audioLocked,
    labels: {
      words: t('library.wordsEntry'),
      audio: t('library.audioEntry'),
      audioVersions: t('library.audioVersions'),
      noWords: t('library.noWordsSlot'),
      noAudio: t('library.noAudioSlot'),
      audioLocked: t('guestSetlist.audioLocked'),
    },
    onWords: openWords,
    onAudio: () => {
      if (row.audioLocked) { opts.onSignIn(); return; }
      if (row.audioPath) opts.navigate(row.audioPath);
    },
  });
  cardRow.appendChild(controls);

  songEl.appendChild(cardRow);
  return songEl;
}

/** Гостевой экран сетлиста. Чистая функция vm → DOM — сеть, роутинг и разбор
 *  `?g=` остаются у вызывающего (design gate: смотрим ЭКРАН, не проводку). */
export function renderGuestSetlist(vm: GuestSetlistVm, handlers: GuestSetlistHandlers): HTMLElement {
  const root = document.createElement('section');
  root.dataset.role = 'guest-setlist-screen';
  root.className = 'ss-root gss-root';

  root.appendChild(buildGuestHeader(handlers.onSignIn));

  const head = document.createElement('header');
  head.className = 'ss-head';
  const h1 = document.createElement('h1');
  h1.className = 'ss-title';
  h1.textContent = vm.title || t('library.untitled');
  head.appendChild(h1);
  if (vm.subtitle) {
    const sub = document.createElement('p');
    sub.className = 'ss-sub';
    sub.textContent = vm.subtitle;
    head.appendChild(sub);
  }
  root.appendChild(head);

  if (vm.live) {
    const live = vm.live;
    const pill = document.createElement('button');
    pill.type = 'button';
    pill.dataset.role = 'setlist-live';
    pill.className = 'ss-live';
    pill.textContent = t('setlistScreen.live', { n: live.songNumber });
    pill.addEventListener('click', () => handlers.navigate(live.path));
    root.appendChild(pill);
  }

  if (vm.rows.length === 0) {
    root.appendChild(buildEmptyState({
      icon: 'musicOff',
      title: t('setlistScreen.empty'),
      dataRole: 'setlist-empty',
    }));
  } else {
    const panel = document.createElement('div');
    panel.className = 'lib-panel';
    const section = document.createElement('section');
    const list = document.createElement('div');
    list.dataset.role = 'songs-list';
    list.className = 'lib-plaque';

    const nowIdx = vm.live && vm.live.songNumber >= 1 && vm.live.songNumber <= vm.rows.length
      ? vm.live.songNumber - 1
      : -1;
    vm.rows.forEach((row, i) => list.appendChild(buildRow(row, i === nowIdx, handlers)));

    section.appendChild(list);
    panel.appendChild(section);
    root.appendChild(panel);
  }

  if (vm.caption) {
    const caption = document.createElement('p');
    caption.className = 'gss-caption';
    caption.dataset.role = 'guest-caption';
    caption.textContent = vm.caption;
    root.appendChild(caption);
  }

  return root;
}

// ── Гостевой доступ закрыт ──────────────────────────────────────────────────

export interface GuestClosedVm {
  /** Имя сетлиста, когда известно (например, прочитано до закрытия доступа).
   *  null — тело без цитаты имени (брифу требуется вариант «имя неизвестно»). */
  name: string | null;
}

export interface GuestClosedHandlers {
  onSignIn: () => void;
}

/** Экран «This link is closed» — 401 по гостевому ключу. Центрированный блок:
 *  замок в круглой нише, заголовок, тело, янтарная кнопка входа (единственное
 *  действие поверхности — она и есть главное действие экрана). */
export function renderGuestClosed(vm: GuestClosedVm, handlers: GuestClosedHandlers): HTMLElement {
  const root = document.createElement('section');
  root.dataset.role = 'guest-closed-screen';
  root.className = 'gss-closed';

  const well = document.createElement('div');
  well.className = 'gss-closed-icon';
  well.innerHTML = icon('lock');
  well.setAttribute('aria-hidden', 'true');
  root.appendChild(well);

  const title = document.createElement('h1');
  title.className = 'gss-closed-title';
  title.textContent = t('guestSetlist.closedTitle');
  root.appendChild(title);

  const body = document.createElement('p');
  body.className = 'gss-closed-body';
  body.textContent = vm.name
    ? t('guestSetlist.closedBodyNamed', { name: vm.name })
    : t('guestSetlist.closedBodyUnnamed');
  root.appendChild(body);

  const signin = document.createElement('bw-button');
  signin.setAttribute('data-action', 'signin');
  signin.setAttribute('shape', 'pill');
  signin.setAttribute('tone', 'amber');
  signin.textContent = t('topbar.signIn');
  signin.addEventListener('bw-click', () => handlers.onSignIn());
  root.appendChild(signin);

  return root;
}
